@if (openSig() && displayMember(); as m) {
  <div class="fixed inset-0 z-50 flex">
    <!-- Backdrop -->
    <div class="absolute inset-0 bg-black/40" (click)="close()"></div>

    <!-- Drawer -->
    <aside class="ml-auto relative h-full w-full sm:w-[480px] bg-white shadow-2xl flex flex-col">
      <!-- Header -->
      <div class="px-5 py-4 border-b border-gray-100 flex items-start gap-3">
        <div class="w-12 h-12 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center text-base font-bold flex-shrink-0">
          {{ getInitials(m.fullName) }}
        </div>
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-2 flex-wrap">
            <h3 class="text-lg font-semibold text-gray-900 truncate">{{ m.fullName }}</h3>
            <span class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium" [class]="statusBadgeClass(m.status)">
              {{ m.status }}
            </span>
          </div>
          @if (m.occupation) {
            <p class="text-xs text-gray-500 mt-0.5 truncate">{{ m.occupation }}</p>
          }
        </div>
        <button type="button" (click)="close()"
                class="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>

      <!-- Body (scrollable) -->
      <div class="flex-1 overflow-y-auto px-5 py-4 space-y-5">

        <!-- Fee snapshot -->
        <section>
          <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Current period</h4>
          <div class="bg-amber-50/40 border border-amber-100 rounded-xl px-3 py-2.5 flex items-center justify-between gap-3">
            <div class="text-sm">
              <div class="text-gray-700">
                Paid <span class="font-semibold">Rs. {{ (m.currentPeriodTotalPaid ?? 0) | number:'1.2-2' }}</span>
                @if ((m.currentPeriodExpected ?? 0) > 0) {
                  <span class="text-gray-500">of Rs. {{ m.currentPeriodExpected | number:'1.2-2' }}</span>
                }
              </div>
              @if (feeShortfall(m) > 0) {
                <div class="text-xs text-amber-700 mt-0.5">Outstanding Rs. {{ feeShortfall(m) | number:'1.2-2' }}</div>
              } @else if ((m.currentPeriodTotalPaid ?? 0) > 0) {
                <div class="text-xs text-green-700 mt-0.5">Fully paid</div>
              }
            </div>
            @if (m.status === 'Active') {
              <button *appHasPermission="301" type="button" (click)="emitRecordPayment()"
                      class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg shadow-sm">
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1"/></svg>
                Record payment
              </button>
            }
          </div>
        </section>

        <!-- Contact -->
        <section>
          <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Contact</h4>
          <dl class="text-sm divide-y divide-gray-50 border border-gray-100 rounded-xl overflow-hidden">
            <div class="flex px-3 py-2">
              <dt class="w-20 text-gray-500">NIC</dt>
              <dd class="flex-1 text-gray-800">{{ m.nic }}</dd>
            </div>
            <div class="flex px-3 py-2">
              <dt class="w-20 text-gray-500">Phone</dt>
              <dd class="flex-1 text-gray-800">{{ m.phone }}</dd>
            </div>
            @if (m.email) {
              <div class="flex px-3 py-2">
                <dt class="w-20 text-gray-500">Email</dt>
                <dd class="flex-1 text-gray-800 break-all">{{ m.email }}</dd>
              </div>
            }
            @if (m.address) {
              <div class="flex px-3 py-2">
                <dt class="w-20 text-gray-500">Address</dt>
                <dd class="flex-1 text-gray-800">{{ m.address }}</dd>
              </div>
            }
            @if (m.joinedDate) {
              <div class="flex px-3 py-2">
                <dt class="w-20 text-gray-500">Joined</dt>
                <dd class="flex-1 text-gray-800">{{ m.joinedDate | date:'mediumDate' }}</dd>
              </div>
            }
          </dl>
        </section>

        <!-- Committees -->
        <section>
          <div class="flex items-center justify-between mb-2">
            <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-500">Committees</h4>
            @if (auth.isSuperAdmin()) {
              <button type="button" (click)="emitManageBusinessUnits()"
                      class="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                Manage
              </button>
            }
          </div>
          @if (m.businessUnitIds && m.businessUnitIds.length > 0) {
            <div class="flex flex-wrap gap-1">
              @for (buId of m.businessUnitIds; track buId) {
                <span [class]="isCurrentBu(buId)
                        ? 'inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-600 text-white'
                        : 'inline-flex px-2 py-0.5 rounded-full text-xs font-medium border border-gray-300 text-gray-600'">
                  {{ buName(buId) }}
                </span>
              }
            </div>
          } @else {
            <p class="text-xs text-amber-600 italic">Unassigned</p>
          }
        </section>

        <!-- Household -->
        @if (m.householdId) {
          <section>
            <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Household</h4>
            <div class="text-sm text-gray-800 flex items-center gap-2 flex-wrap">
              <span class="font-mono text-xs">{{ m.householdId }}</span>
              @if (m.householdRole) {
                <span class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                  {{ m.householdRole }}
                </span>
              }
              <button type="button" (click)="emitSelectHousehold()"
                      class="text-xs text-blue-600 hover:underline">
                Select household
              </button>
            </div>
          </section>
        }

        <!-- Account -->
        <section>
          <div class="flex items-center justify-between mb-2">
            <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-500">Account</h4>
            <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                  [class]="accountPillClass(m)">
              @if (m.userAccount) {
                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7"/>
                </svg>
              }
              {{ accountStatusLabel(m) }}
            </span>
          </div>
          @if (m.userAccount) {
            <p class="text-xs text-gray-500 mb-2">Username: <span class="font-mono">{{ m.userAccount.username }}</span></p>
            <div class="flex flex-wrap gap-2">
              <button type="button" (click)="emitResetPassword()"
                      [disabled]="busy.reset || busy.invite || busy.deactivate"
                      class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
                @if (busy.reset) {
                  <app-spinner size="xs" colour="current"></app-spinner>
                }
                Reset password
              </button>
              <button type="button" (click)="emitManageRoles()"
                      [disabled]="busy.reset || busy.invite || busy.deactivate"
                      class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
                Manage roles
              </button>
              <button type="button" (click)="emitInvite()"
                      [disabled]="busy.reset || busy.invite || busy.deactivate"
                      class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">
                @if (busy.invite) {
                  <app-spinner size="xs" colour="current"></app-spinner>
                }
                Send invitation
              </button>
              @if (m.userAccount.status === 'Active') {
                <button type="button" (click)="emitDeactivate()"
                        [disabled]="busy.reset || busy.invite || busy.deactivate"
                        class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-red-200 rounded-lg hover:bg-red-50 text-red-600 disabled:opacity-50 disabled:cursor-not-allowed">
                  @if (busy.deactivate) {
                    <app-spinner size="xs" colour="current"></app-spinner>
                  }
                  Deactivate
                </button>
              }
            </div>
          } @else {
            <button type="button" (click)="emitCreateUserAccount()"
                    [disabled]="busy.createUser"
                    class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-blue-200 rounded-lg hover:bg-blue-50 text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              @if (busy.createUser) {
                <app-spinner size="xs" colour="current"></app-spinner>
              }
              Create user account
            </button>
          }
        </section>

        @if (m.notes) {
          <section>
            <h4 class="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Notes</h4>
            <p class="text-sm text-gray-700 whitespace-pre-wrap">{{ m.notes }}</p>
          </section>
        }

        <app-custom-fields-input [readOnly]="true" formKey="Member"
          [value]="m.customFields || {}"></app-custom-fields-input>
      </div>

      <!-- Footer -->
      <div class="px-5 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-3 border-t border-gray-100 flex items-center justify-between gap-2">
        <button *appHasPermission="203" type="button" (click)="emitEdit()"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-700">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
          </svg>
          Edit member
        </button>
        <button type="button" (click)="emitOpenFullProfile()"
                class="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:underline">
          Open full profile
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
          </svg>
        </button>
      </div>
    </aside>
  </div>
}
